/**
 * detectAiPrompt — heuristic that flags text that looks like a transformation
 * meta-prompt (what the user should run as a Follow-up question) rather than
 * raw content to summarize.
 *
 * Rationale: prompt-type cas pratiques (QCM, mail parents, DYS, plan de
 * séance, correction type, 3 idées débat…) are instructions, not source
 * material. Users who copy them from a book or external list and paste them
 * directly into the Dashboard textarea end up with SUX1 "summarizing the
 * prompt" — a useless output. We detect it client-side and offer a 1-click
 * shift to the "Question de suivi" workflow.
 *
 * Detection is deliberately simple:
 *   1. Starts with a French imperative verb (short list of the most common
 *      teaching-prompt openers)
 *   2. AND is reasonably short (meta-prompts rarely exceed ~800 chars,
 *      whereas real source texts are usually 1-10k)
 *
 * False positives are OK — a dismissible banner is far cheaper than letting
 * the teacher burn a quota on a mis-targeted summary.
 */
const IMPERATIVE_VERBS = [
  "réécris",
  "rédige",
  "transforme",
  "reformule",
  "génère",
  "produis",
  "crée",
  "propose",
  "adapte",
  "écris",
  "liste",
  "analyse",
  "compare",
  "simplifie",
  "classe",
  "corrige",
  "annote",
  "note",
  "résous",
  "explique",
  "détaille",
  "élabore",
  "conçois",
  "prépare",
  "construis",
  "formule",
  "imagine",
  "invente",
  "fabrique",
  "dessine",
  "schématise",
  "traduis",
  "convertis",
  "scanne",
];

const EXTERNAL_CONTENT_REFERENCES = [
  "cette consigne",
  "ce texte",
  "ce document",
  "ce cours",
  "ce chapitre",
  "cette copie",
  "cet élève",
  "cette évaluation",
  "ce manuel",
  "cet exercice",
  "cette leçon",
  "ce résumé",
];

export function detectAiPrompt(text) {
  if (!text || typeof text !== "string") return false;
  const trimmed = text.trim();
  if (trimmed.length < 25) return false; // noise
  if (trimmed.length > 1200) return false; // real content, not a prompt

  const normalised = trimmed.toLowerCase();
  const firstWord = normalised.split(/\s+/)[0].replace(/[^a-zàâäéèêëîïôöùûüç']/g, "");

  const startsWithImperative = IMPERATIVE_VERBS.some((v) => firstWord === v);
  const referencesExternal = EXTERNAL_CONTENT_REFERENCES.some((ref) =>
    normalised.includes(ref)
  );

  // Primary signal: imperative at the start (very strong — real articles
  // never open with "Réécris ...").
  // Secondary boost: reference to external content ("cette consigne",
  // "ce texte"…) is a near-certain prompt pattern.
  return startsWithImperative || referencesExternal;
}
